import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { getConnectedDevice, getDeviceInstallerError } from "../state/deviceInstallerSlice";
import { requestAndConnect, retryAuthentication, disconnect } from "../utils/adbConnection";
import { isWebUsbSupported } from "../utils/browserCompat";
import { usePackagePolling } from "../hooks/usePackagePolling";
import { DevicePlainIcon, Spinner } from "./icons";

const ERROR_TRANSLATION_MAP: Record<string, string> = {
  securityError: "deviceInstaller.errorSecurityError",
  notFound: "deviceInstaller.errorNotFound",
  deviceBusy: "deviceInstaller.errorDeviceBusy",
  accessDenied: "deviceInstaller.errorAccessDenied",
};

const AUTH_SHOW_HINT_MS = 2000;
const AUTH_TIMEOUT_MS = 12000;

const DeviceConnectionBar = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const device = useAppSelector(getConnectedDevice);
  const error = useAppSelector(getDeviceInstallerError);
  const [showAuthHint, setShowAuthHint] = useState(false);
  const [authTimedOut, setAuthTimedOut] = useState(false);

  usePackagePolling();

  const isAuthenticating = device?.status === "authenticating";

  useEffect(() => {
    if (!isAuthenticating) {
      setShowAuthHint(false);
      setAuthTimedOut(false);
      return undefined;
    }
    const hintTimer = setTimeout(() => setShowAuthHint(true), AUTH_SHOW_HINT_MS);
    const timeoutTimer = setTimeout(() => setAuthTimedOut(true), AUTH_TIMEOUT_MS);
    return () => {
      clearTimeout(hintTimer);
      clearTimeout(timeoutTimer);
    };
  }, [isAuthenticating]);

  if (!isWebUsbSupported()) {
    return (
      <span className="text-gray-400 text-sm">{t("deviceInstaller.browserNotSupported")}</span>
    );
  }

  const handleConnect = async () => {
    await requestAndConnect(dispatch);
  };

  const handleRetryAuth = async () => {
    await retryAuthentication(dispatch);
  };

  const handleDisconnect = async () => {
    await disconnect(dispatch);
  };

  const isConnecting = device?.status === "connecting";
  const isReady = device?.status === "ready";
  const hasError = device?.status === "error";

  const translateError = (msg: string | undefined): string => {
    if (!msg) return "";
    const key = ERROR_TRANSLATION_MAP[msg];
    return key ? t(key) : msg;
  };

  return (
    <div className="flex items-center gap-3 min-w-0">
      {!device && (
        <span className="relative inline-block group">
          <button
            onClick={handleConnect}
            className="flex items-center gap-2 px-3 py-1.5 text-white bg-green-500 rounded-md text-sm hover:opacity-90 transition-opacity whitespace-nowrap"
          >
            <DevicePlainIcon />
            {t("deviceInstaller.connect")}
          </button>
          <div className="absolute left-0 top-full mt-1 z-50 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none">
            <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 w-72 shadow-lg whitespace-normal font-normal">
              {t("deviceManager.devModeNotice")}
            </div>
          </div>
        </span>
      )}

      {isConnecting && (
        <div className="flex items-center gap-2 text-sm text-green-500">
          <Spinner color="green" />
          <span>{t("deviceInstaller.deviceConnecting")}</span>
        </div>
      )}

      {isAuthenticating && (
        <div className="flex items-center gap-2">
          <Spinner color="green" />
          {!showAuthHint ? (
            <span className="text-sm text-green-500">
              {t("deviceInstaller.deviceAuthenticating")}
            </span>
          ) : (
            <div className="flex flex-col">
              <div className="flex items-center gap-1.5 text-sm text-green-500">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v2a2 2 0 002 2h2m10-4v6m0-6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2m-10 0h10M5 11V7a7 7 0 0114 0v4"
                  />
                </svg>
                <span>{t("deviceInstaller.authInstructions")}</span>
              </div>
              {authTimedOut && (
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-amber-500">{t("deviceInstaller.authTimeout")}</span>
                  <button
                    onClick={handleRetryAuth}
                    className="px-2 py-0.5 text-xs rounded hover:opacity-80 transition-opacity text-green-500 border border-green-500"
                  >
                    {t("deviceInstaller.retry")}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {isReady && (
        <>
          <div className="flex items-center gap-2 text-sm text-green-500">
            <div className="w-2 h-2 rounded-full bg-green-500" />
            <span className="font-medium">
              {t("deviceInstaller.connected", { name: device.productName })}
            </span>
            <span className="text-xs text-gray-400">{device.serial}</span>
          </div>
          <button
            onClick={handleDisconnect}
            className="px-2 py-1 text-xs text-gray-400 hover:text-gray-600 border border-gray-200 rounded hover:bg-gray-50 transition-colors"
          >
            {t("deviceInstaller.disconnect")}
          </button>
        </>
      )}

      {hasError && (
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="text-sm text-red-500">{translateError(device.errorMessage)}</span>
            <button
              onClick={handleConnect}
              className="px-2 py-1 text-xs rounded hover:opacity-80 transition-opacity text-green-500 border border-green-500"
            >
              {t("deviceInstaller.retry")}
            </button>
          </div>
          {device.errorMessage === "deviceBusy" && (
            <span
              className="text-xs text-gray-500 [&>code]:bg-gray-200 [&>code]:px-1 [&>code]:py-0.5 [&>code]:rounded [&>code]:font-mono [&>code]:text-red-600"
              dangerouslySetInnerHTML={{ __html: t("deviceInstaller.errorDeviceBusyHint") }}
            />
          )}
        </div>
      )}

      {error && !device && <span className="text-sm text-red-500">{translateError(error)}</span>}
    </div>
  );
};

export default DeviceConnectionBar;
